// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// Shared scripts (API + client)
import { activityTypes, ACTIVITY_METADATA_MAX_BYTES } from '@fontan/shared';

// Local scripts
import { getStoredRecordings } from '../scripts/backupRecordings.js';



// ----------------------------------------------------------------------------
//   Setup
// ----------------------------------------------------------------------------


const SERVER_URL = process.env.REACT_APP_SERVER_URL;


/**
 * Activity types that get a summary of the locally stored recordings attached,
 * to help debug uploads. Other activities (play, pause, etc.) don't need it.
 */
const LOCAL_RECORDINGS_ACTIVITY_TYPES = new Set([
    activityTypes.USER_CLICK_UPLOAD,
    activityTypes.VIDEO_UPLOAD_START,
    activityTypes.VIDEO_UPLOAD_COMPLETE,
    activityTypes.VIDEO_UPLOAD_FAILED,
    activityTypes.ENTERED_ERROR_STATE,
]);

/** Max time to wait on IndexedDB before sending the activity without the summary. */
const LOCAL_RECORDINGS_TIMEOUT_MS = 1500;

/** Number of most recent local recordings to describe individually in the summary. */
const LOCAL_RECORDINGS_RECENT_COUNT = 5;



// ----------------------------------------------------------------------------
//   Helpers
// ----------------------------------------------------------------------------


/**
 * Summarize the locally stored recordings: counts plus the last few recordings.
 * Never rejects, and gives up after a short timeout so a hung IndexedDB
 * (e.g. Safari private mode, blocked upgrade) can't stop the activity being sent.
 */
async function summarizeLocalRecordings() {
    let timer;
    const timeout = new Promise((resolve) => {
        timer = setTimeout(() => resolve({ error: `Timed out after ${LOCAL_RECORDINGS_TIMEOUT_MS}ms` }), LOCAL_RECORDINGS_TIMEOUT_MS);
    });

    const lookup = getStoredRecordings()
        .then((recordings) => ({
            count:         recordings.length,
            uploadedCount: recordings.filter((r) => r.uploaded).length,
            recent:        recordings.slice(-LOCAL_RECORDINGS_RECENT_COUNT).map((r) => ({
                recordingID: r.recordingID,
                title:       r.title,
                filename:    r.filename,
                size:        r.blob?.size,
                uploaded:    r.uploaded,
                uploadedAt:  r.uploadedAt,
            })),
        }))
        .catch((err) => ({
            error: err?.message ?? String(err),
        }));

    try {
        return await Promise.race([lookup, timeout]);
    }
    finally {
        clearTimeout(timer);
    }
}


/**
 * Device hints that the User-Agent header alone gets wrong
 * (e.g. iPadOS 13+ reports itself as macOS).
 */
function getClientHints() {
    return {
        maxTouchPoints: navigator?.maxTouchPoints,
        uaPlatform:     navigator?.userAgentData?.platform,
        uaMobile:       navigator?.userAgentData?.mobile,
    };
}



// ----------------------------------------------------------------------------
//   API Calls
// ----------------------------------------------------------------------------


export const fetchCompletedVideos = async () => {
    try {
        const resp = await fetch(`${SERVER_URL}/completed-videos`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include'
        })
        if (resp.status === 200) {
            const data = await resp.json();
            // return an array of video IDs
            return data.map((activity) => activity.video_id);
        } 
    } catch(err) {
        throw new Error('Error fetching videos:', err);
    }
}

export const createActivity = async (activityType, videoName = null, metadata = null) => {

    // Add internal field(s) to metadata. Uses `_`-prefixed keys to avoid collisions
    // with caller-supplied metadata fields.
    const enrichedMetadata = {
        ...metadata,
        _client: getClientHints(),
    };

    // For upload / error activities, attach a capped summary of the local recordings
    if (LOCAL_RECORDINGS_ACTIVITY_TYPES.has(activityType)) {
        enrichedMetadata._localRecordings = await summarizeLocalRecordings();

        // Stay within the server's metadata size cap, dropping the per-recording details first
        if (JSON.stringify(enrichedMetadata).length > ACTIVITY_METADATA_MAX_BYTES) {
            const { recent, ...counts } = enrichedMetadata._localRecordings;
            enrichedMetadata._localRecordings = counts;
        }
    }

    try {
        const res = await fetch(`${SERVER_URL}/activity/create`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ activityType, videoName, metadata: enrichedMetadata }),
        })
        return res.json();
    } catch(err) {
        throw new Error(`Error creating activity: ${err?.message}`, { cause: err });
    }
}

export const fetchStudentActivities = async (userID) => {
    const resp = await fetch(`${SERVER_URL}/student/${userID}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    if (resp.ok) {
        return resp.json();
    } else {
        throw new Error(`Error fetching student activities: ${resp.status}`);
    }
}



// ----------------------------------------------------------------------------
//   Export(s)
// ----------------------------------------------------------------------------


export default {
    activityTypes,
    fetchCompletedVideos,
    createActivity,
    fetchStudentActivities,
};
